import React, { useState } from "react";
import { Phone, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { WfButton } from "@/components/site/primitives";
import { ORG, PHOTO_CREDIT } from "@/content/worldFarmers";
import { PRIMARY_NAV, FOOTER_UTILITY_LINKS } from "@/content/nav";

// Keep the footer light: the header already exposes every sub-page, so the
// footer lists only the four top-level sections plus a few high-value shortcuts.
const EXPLORE_LINKS = PRIMARY_NAV.map((item) => ({ to: item.href, label: item.label }));
const QUICK_LINKS = [
  { to: "/support", label: "Donate" },
  { to: "/csa", label: "CSA Sign-Up" },
  { to: "/farmers-markets", label: "Farmers Markets" },
  { to: "/events", label: "Events" },
];

const footerLinkClass =
  "text-[rgba(251,247,239,0.78)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--wf-moss)] rounded-sm";

export const SiteFooter = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const onSubmit = (e) => {
    e.preventDefault();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!valid) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setEmail("");
    toast.success("Thanks for subscribing!", {
      description: "You’re on the list — we’ll be in touch with news from the farm.",
    });
  };

  return (
    <footer data-testid="site-footer" className="bg-[var(--wf-soil)] text-[var(--wf-cream)]">
      <div className="wf-container py-14 lg:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          {/* Brand + contact */}
          <div className="md:col-span-5 lg:col-span-4">
            <img src="/images/logo-white.png" alt="World Farmers" className="h-11 w-auto" />
            <p className="mt-4 max-w-sm text-sm leading-6 text-[rgba(251,247,239,0.72)]">
              {ORG.missionShort}
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-[var(--wf-moss)]" aria-hidden="true" />
                <a href="mailto:info@worldfarmers.org" className="text-[rgba(251,247,239,0.9)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--wf-moss)] rounded-sm">
                  info@worldfarmers.org
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-[var(--wf-moss)]" aria-hidden="true" />
                <a href={ORG.phoneHref} className="text-[rgba(251,247,239,0.9)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--wf-moss)] rounded-sm">
                  {ORG.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[var(--wf-moss)]" aria-hidden="true" />
                <span className="not-italic text-[rgba(251,247,239,0.9)]">
                  {ORG.addressLines.map((l) => (<span key={l} className="block">{l}</span>))}
                </span>
              </li>
            </ul>
          </div>

          {/* Explore + quick links */}
          <nav aria-label="Footer" className="md:col-span-7 lg:col-span-4">
            <div className="grid grid-cols-2 gap-x-8 gap-y-8">
              <div data-testid="footer-explore">
                <h2 className="font-serif text-lg font-semibold text-[var(--wf-cream)]">Explore</h2>
                <ul className="mt-4 space-y-3 text-sm">
                  {EXPLORE_LINKS.map((l) => (
                    <li key={l.to}><Link to={l.to} className={footerLinkClass}>{l.label}</Link></li>
                  ))}
                </ul>
              </div>
              <div data-testid="footer-quick-links">
                <h2 className="font-serif text-lg font-semibold text-[var(--wf-cream)]">Quick links</h2>
                <ul className="mt-4 space-y-3 text-sm">
                  {QUICK_LINKS.map((l) => (
                    <li key={l.to}><Link to={l.to} className={footerLinkClass}>{l.label}</Link></li>
                  ))}
                </ul>
              </div>
            </div>
          </nav>

          {/* Subscribe (UI-only) */}
          <div className="md:col-span-12 lg:col-span-4">
            <h2 className="font-serif text-lg font-semibold text-[var(--wf-cream)]">Join our email list</h2>
            <p className="mt-2 text-sm text-[rgba(251,247,239,0.72)]">
              News from the farm, event invites, and stories from our growers.
            </p>
            <form onSubmit={onSubmit} data-testid="footer-subscribe-form" noValidate className="mt-4">
              <label htmlFor="subscribe-email" className="sr-only">Email address</label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgba(251,247,239,0.6)]" aria-hidden="true" />
                  <input
                    id="subscribe-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={error ? "true" : "false"}
                    aria-describedby="footer-subscribe-helper"
                    data-testid="footer-subscribe-email-input"
                    className="min-h-11 w-full rounded-lg border border-[rgba(251,247,239,0.2)] bg-[rgba(251,247,239,0.06)] py-2.5 pl-9 pr-3 text-sm text-[var(--wf-cream)] placeholder:text-[rgba(251,247,239,0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--wf-moss)]"
                  />
                </div>
                <WfButton type="submit" variant="light" data-testid="footer-subscribe-submit-button" className="shrink-0">
                  Subscribe
                </WfButton>
              </div>
              <p
                id="footer-subscribe-helper"
                data-testid="footer-subscribe-helper-text"
                className={`mt-2 text-xs ${error ? "text-[#ffb4a2]" : "text-[rgba(251,247,239,0.55)]"}`}
                role={error ? "alert" : undefined}
              >
                {error || "We’ll never share your email. Unsubscribe anytime."}
              </p>
            </form>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-[rgba(251,247,239,0.14)] pt-6 text-xs text-[rgba(251,247,239,0.6)] sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} {ORG.name}. A nonprofit organization based in Lancaster, Massachusetts.</p>
          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2" aria-label="Utility links" data-testid="footer-utility-links">
            {FOOTER_UTILITY_LINKS.map((l) => (
              <li key={l.id}><Link to={l.href} className="hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--wf-moss)] rounded-sm">{l.label}</Link></li>
            ))}
          </ul>
          <p>
            {"Photography: "}
            <a href={PHOTO_CREDIT.href} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-2 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--wf-moss)] rounded-sm">
              Emily Joy Ashman
            </a>
            {" and Vlai Ly, The Transient Image"}
          </p>
        </div>
      </div>
    </footer>
  );
};
